import { cn } from '@/shared/lib'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedProps<T extends string> {
  // Что именно переключает группа — иначе в тулбаре из нескольких групп
  // скринридер читает подряд четыре кнопки без признака, где чей набор.
  ariaLabel: string
  value: T
  options: SegmentedOption<T>[]
  onChange: (value: T) => void
  className?: string
}

/**
 * Переключатель одного из вариантов: общая подложка держит набор как единое
 * целое. Соседние группы в тулбаре («вся команда / только я» и «день / неделя»)
 * иначе читаются одним рядом равных опций, и непонятно, что с чем спорит.
 *
 * touch: мишень добирается по вертикали — в поле по тулбару попадают пальцем.
 */
export function Segmented<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
  className,
}: SegmentedProps<T>) {
  return (
    <fieldset
      aria-label={ariaLabel}
      className={cn(
        'm-0 inline-flex shrink-0 items-center gap-0.5 rounded-pill border-0 bg-bg-surface p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-pill px-3 pt-[7px] pb-[5px] font-mono text-xs whitespace-nowrap uppercase tracking-wide transition-colors touch:pt-2.5 touch:pb-2',
              active
                ? 'bg-bg-raised text-text-primary'
                : 'text-text-tertiary hover:text-text-secondary',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </fieldset>
  )
}
